import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateParam } from "helpers";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import { getOverviewOffersAPI } from "services/api/dashboard/api";
import { OverviewOffersResponse } from "services/api/dashboard/types";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../common/DashboardTooltip";
import CardHeader, { DeltaBadge } from "../../common/CardHeader";
import BarTooltip, { BarTooltipRow, BarTooltipTitle, LegendTooltip } from "../../common/HoverPopup";
import { compact, positive, text } from "../../common/dashboardPalette";
import { useDashboardDateRange } from "../../../context/DashboardDateRangeContext";
import { rangeLabel } from "ui/components/DateRangePicker";
import AxisLabels from "../../common/AxisLabels";

const OffersCard: React.FC = () => {
  const navigate = useNavigate();
  const { range } = useDashboardDateRange();
  const [data, setData] = useState<OverviewOffersResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getOverviewOffersAPI(range.from, range.to)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, [range.from, range.to]);

  if (isLoading) return <div className="relative h-full"><Loader isLoading={true}> </Loader></div>;
  if (!data) return <div className="flex items-center justify-center h-full text-sm text-slate-400">Nema podataka</div>;

  const months = data.months.map((m) => ({
    label: m.monthLabel || m.month,
    start: m.start,
    end: m.end,
    sent: m.sent,
    accepted: m.accepted,
    paid: m.paid,
  }));

  // Klik na seriju -> pretraga ponuda po statusu + periodu (landa na tab "Prodaja"; isti `status` param
  // čita i tab "Najam"). Bucket `end` je ekskluzivan pa ga za inkluzivni "Ponuda do" pomičemo za 1 ms unatrag.
  const handleBarClick = (status: string, start: string | null, end: string | null) => {
    const params = new URLSearchParams({ tab: "sale", status });
    if (start) params.set("offerDateFrom", formatDateParam(new Date(start))!);
    if (end) params.set("offerDateTo", formatDateParam(new Date(new Date(end).getTime() - 1))!);
    navigate(`${FinanceRoutes.OfferSearchPageRouter}?${params.toString()}`);
  };

  const sent = months.map((m) => m.sent);
  const accepted = months.map((m) => m.accepted);
  const paid = months.map((m) => m.paid);
  const maxVal = Math.max(...sent, ...accepted, ...paid, 1);

  const totalSent = data.totalSent;
  const totalAccepted = data.totalAccepted;
  const totalPaid = data.totalPaid;
  const acceptanceRate = totalSent > 0 ? (totalAccepted / totalSent) * 100 : 0;
  const paymentRate = totalAccepted > 0 ? (totalPaid / totalAccepted) * 100 : 0;

  const last = months.length - 1;
  const prev = last - 1;
  const sentChange = prev >= 0 && sent[prev] > 0 ? ((sent[last] - sent[prev]) / sent[prev]) * 100 : 0;
  const accChange = prev >= 0 && accepted[prev] > 0 ? ((accepted[last] - accepted[prev]) / accepted[prev]) * 100 : 0;
  const paidChange = prev >= 0 && paid[prev] > 0 ? ((paid[last] - paid[prev]) / paid[prev]) * 100 : 0;

  // Series mapping: 0 = sky (sent), 1 = indigo (accepted, in pipeline), 2 = emerald (paid — won)
  // Prozirni gradient (jak na vrhu -> blijedi prema dnu) + 2px border na vrhu, kao ostale kartice.
  // Zadržane postojeće boje serija. Tekući mjesec zasićeniji. Pune klase u stringu za Tailwind JIT.
  const sentBarBase = "bg-gradient-to-b from-sky-500/70 to-sky-500/30 border-t-2 border-sky-500 group-hover/bar:from-sky-500/80 group-hover/bar:to-sky-500/40";
  const sentBarCurrent = "bg-gradient-to-b from-sky-500/90 to-sky-500/40 border-t-2 border-sky-500 group-hover/bar:from-sky-500 group-hover/bar:to-sky-500/50";
  const accBarBase = "bg-gradient-to-b from-yellow-300/80 to-yellow-300/25 border-t-2 border-yellow-400 group-hover/bar:from-yellow-300/90 group-hover/bar:to-yellow-300/35";
  const accBarCurrent = "bg-gradient-to-b from-yellow-400/90 to-yellow-400/30 border-t-2 border-yellow-400 group-hover/bar:from-yellow-400 group-hover/bar:to-yellow-400/40";
  const paidBarBase = "bg-gradient-to-b from-emerald-500/70 to-emerald-500/30 border-t-2 border-emerald-500 group-hover/bar:from-emerald-500/80 group-hover/bar:to-emerald-500/40";
  const paidBarCurrent = "bg-gradient-to-b from-emerald-500/90 to-emerald-500/40 border-t-2 border-emerald-500 group-hover/bar:from-emerald-500 group-hover/bar:to-emerald-500/50";
  // Statičke klase za legendu / dot indikatore.
  const sentLegend = "bg-sky-500";
  const accLegend = "bg-yellow-400";
  const paidLegend = positive.solid;

  return (
    <div className={`rounded-sm ${compact.padding} bg-white h-full flex flex-col`}>
      <CardHeader
        title="Ponude"
        subtitle={`Pregled · ${rangeLabel(range)}`}
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help gap-1">
                <span className={`${compact.metric} font-bold ${text.primary}`}>{totalSent}</span>
                <DeltaBadge value={paymentRate} decimals={0} />
              </div>
            }
          >
            <TooltipTitle>Pregled ponuda</TooltipTitle>
            <TooltipRow label="Poslano" value={totalSent} />
            <TooltipRow label="Prihvaćeno" value={`${totalAccepted} (${acceptanceRate.toFixed(1)}%)`} />
            <TooltipRow label="Plaćeno" value={`${totalPaid} (${paymentRate.toFixed(1)}%)`} />
            <TooltipDivider />
            <TooltipRow
              label="Stopa konverzije"
              value={`${(totalSent > 0 ? totalPaid / totalSent * 100 : 0).toFixed(1)}%`}
              emphasis="positive"
            />
          </DashboardTooltip>
        }
      />

      {data.totalSent === 0 ? (
        <NoResults title="Nema ponuda" />
      ) : (
        <>
      <div className="mx-auto mb-2 w-full flex-1 flex flex-col">
        <div className="flex justify-between gap-2 mb-2 flex-1 min-h-[120px]">
          {months.map((m, i) => {
            const sentHeight = (sent[i] / maxVal) * 100;
            const accHeight = (accepted[i] / maxVal) * 100;
            const paidHeight = (paid[i] / maxVal) * 100;
            const isCurrent = i === last;
            return (
              <div
                key={`${m.label}-${i}`}
                className="flex flex-col items-center flex-1 h-full group/bar relative"
              >
                <div className="w-full flex-1 flex items-end justify-center gap-0.5">
                  <div
                    className={`flex-1 rounded-sm origin-bottom animate-growUp transition-all cursor-pointer ${isCurrent ? sentBarCurrent : sentBarBase}`}
                    style={{ height: `${Math.max(sentHeight, 5)}%` }}
                    onClick={() => handleBarClick("sent", m.start, m.end)}
                  />
                  <div
                    className={`flex-1 rounded-sm origin-bottom animate-growUp transition-all cursor-pointer ${isCurrent ? accBarCurrent : accBarBase}`}
                    style={{ height: `${Math.max(accHeight, 5)}%` }}
                    onClick={() => handleBarClick("accepted", m.start, m.end)}
                  />
                  <div
                    className={`flex-1 rounded-sm origin-bottom animate-growUp transition-all cursor-pointer ${isCurrent ? paidBarCurrent : paidBarBase}`}
                    style={{ height: `${Math.max(paidHeight, 5)}%` }}
                    onClick={() => handleBarClick("paid", m.start, m.end)}
                  />
                </div>
                <BarTooltip>
                  <BarTooltipTitle>{m.label}</BarTooltipTitle>
                  <BarTooltipRow label="Poslano" value={sent[i]} />
                  <BarTooltipRow label="Prihvaćeno" value={accepted[i]} />
                  <BarTooltipRow label="Plaćeno" value={paid[i]} emphasis="positive" />
                </BarTooltip>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-2">
          <AxisLabels labels={months.map((m) => m.label)} className={`text-sm ${text.subtle}`} />
        </div>
      </div>

      <div className="relative group flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-3 cursor-help">
        <div className="flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${sentLegend}`} />
          <span className={`text-sm ${text.muted}`}>Poslane <span className="font-medium text-slate-700">{totalSent}</span></span>
          <DeltaBadge value={sentChange} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${accLegend}`} />
          <span className={`text-sm ${text.muted}`}>Prihvaćene <span className="font-medium text-slate-700">{totalAccepted}</span></span>
          <DeltaBadge value={accChange} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${paidLegend}`} />
          <span className={`text-sm ${text.muted}`}>Plaćene <span className="font-medium text-slate-700">{totalPaid}</span></span>
          <DeltaBadge value={paidChange} />
        </div>
        <LegendTooltip>
          <BarTooltipTitle>Promjena vs prethodni mjesec</BarTooltipTitle>
          <BarTooltipRow label="Poslane" value={`${prev >= 0 ? sent[prev] : 0} → ${sent[last] ?? 0}`} />
          <BarTooltipRow label="Prihvaćene" value={`${prev >= 0 ? accepted[prev] : 0} → ${accepted[last] ?? 0}`} />
          <BarTooltipRow label="Plaćene" value={`${prev >= 0 ? paid[prev] : 0} → ${paid[last] ?? 0}`} emphasis="positive" />
        </LegendTooltip>
      </div>
        </>
      )}
    </div>
  );
};

export default OffersCard;
